/**
 * Escala dos símbolos pontuais com o zoom — a regra é UMA, para todas as
 * camadas (radiossonda, meteorologia, pluviômetros, qualidade do ar, rios,
 * embarcações e focos). Ver `docs/proposta-icones-zoom.md`.
 *
 * Com o tamanho fixo em pixels, de longe (a Amazônia inteira na tela) as
 * camadas densas viravam uma mancha, e de perto os símbolos ficavam pequenos
 * para o dedo. A radiossonda já crescia com o zoom, mas com uma curva própria
 * que triplicava o tamanho e saturava cedo (no nível 6), grande demais.
 *
 * A curva: **um décimo por nível de zoom**, valendo 1 no nível de referência
 * (o 6, onde a densidade das camadas já é confortável e o tamanho fica igual
 * ao de antes), de 0,7 (nível 3 para fora) a 1,3 (nível 9 para dentro). É
 * linear no NÍVEL de zoom, isto é, no logaritmo da resolução: cada nível muda
 * o símbolo na mesma medida, sem saltos e sem saturar cedo.
 *
 * O fator sai em DEGRAUS de 0,1 — sete valores ao todo —, porque entra na
 * chave dos caches de sprite e de estilo: contínuo, cada gesto de zoom criaria
 * estilos novos sem parar.
 *
 * Cada camada multiplica o SEU tamanho base (e o controle de tamanho do
 * painel) por este fator; como a curva é a mesma, a proporção entre os
 * símbolos de camadas diferentes é igual em qualquer zoom.
 *
 * O que NÃO escala: a espessura dos contornos e dos traços (continuam em px de
 * tela, como a radiossonda sempre fez — um contorno de 1 px encolhido some, e
 * engrossado borra o símbolo), os tracejados e a folga dos anéis e dos selos.
 */
import { MAX_RESOLUTION } from './createMap';

/** Nível de zoom em que o fator vale 1 (o tamanho de antes da escala). */
export const ZOOM_DE_REFERENCIA = 6;
/** Resolução (unidades do mapa por px) do nível de referência. */
export const RESOLUCAO_DE_REFERENCIA = MAX_RESOLUTION / 2 ** ZOOM_DE_REFERENCIA;
/** Quanto o fator muda a cada nível de zoom. */
export const GANHO_POR_NIVEL = 0.1;
export const FATOR_MIN = 0.7;
export const FATOR_MAX = 1.3;
/** Degrau do fator (o que entra nas chaves de cache). */
export const DEGRAU = 0.1;

/** Todos os valores que o fator pode assumir, do menor ao maior. */
export const FATORES: readonly number[] = (() => {
  const n = Math.round((FATOR_MAX - FATOR_MIN) / DEGRAU);
  return Array.from({ length: n + 1 }, (_, i) => arredondar(FATOR_MIN + i * DEGRAU));
})();

function arredondar(f: number): number {
  return Math.round(f / DEGRAU) / Math.round(1 / DEGRAU);
}

/**
 * Fator de escala dos símbolos na resolução dada. Sem resolução (estilo
 * avaliado fora do mapa), 1.
 */
export function fatorDoZoom(resolution: number | undefined): number {
  if (!resolution || !(resolution > 0) || !Number.isFinite(resolution)) return 1;
  const niveis = Math.log2(RESOLUCAO_DE_REFERENCIA / resolution);
  const f = 1 + GANHO_POR_NIVEL * niveis;
  return arredondar(Math.min(FATOR_MAX, Math.max(FATOR_MIN, f)));
}

/** O mesmo fator, a partir do nível de zoom (para tabelas e testes). */
export function fatorNoNivel(zoom: number): number {
  return fatorDoZoom(MAX_RESOLUTION / 2 ** zoom);
}

/**
 * Densidade de pixels usada nos sprites, limitada a 3. Entra nas chaves de
 * cache: arrastar a janela de um monitor 2× para um 1× não pode reaproveitar o
 * canvas antigo com a escala nova.
 */
export function dpr(): number {
  return Math.min(3, Math.max(1, (typeof window === 'undefined' ? 1 : window.devicePixelRatio) || 1));
}

/**
 * Cache com teto: um `Map` que, cheio, descarta a entrada usada há mais tempo.
 *
 * Os caches de estilo têm chaves com cor, opacidade, rumo, texto e — agora — o
 * fator do zoom. Cada parte é discreta, mas o produto delas não tem teto
 * prático numa sessão longa; este tem.
 */
export class CacheLimitado<V> {
  private readonly mapa = new Map<string, V>();

  constructor(readonly limite: number) {}

  get(k: string): V | undefined {
    const v = this.mapa.get(k);
    if (v !== undefined) {
      // Reinsere no fim: a ordem do `Map` passa a ser a de uso.
      this.mapa.delete(k);
      this.mapa.set(k, v);
    }
    return v;
  }

  set(k: string, v: V): void {
    this.mapa.delete(k);
    this.mapa.set(k, v);
    while (this.mapa.size > this.limite) {
      const maisAntiga = this.mapa.keys().next().value;
      if (maisAntiga === undefined) break;
      this.mapa.delete(maisAntiga);
    }
  }

  get size(): number {
    return this.mapa.size;
  }

  clear(): void {
    this.mapa.clear();
  }
}

/** Caches registrados pelas camadas, para medir (`tamanhoDosCaches`). */
const registrados = new Map<string, { readonly size: number }>();

/** Cria um cache com teto e o registra pelo nome (ex.: `meteo.setas`). */
export function criarCache<V>(nome: string, limite: number): CacheLimitado<V> {
  const c = new CacheLimitado<V>(limite);
  registrados.set(nome, c);
  return c;
}

/** Entradas em cada cache de símbolos — usado nas medições de desempenho. */
export function tamanhoDosCaches(): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [nome, c] of registrados) out[nome] = c.size;
  return out;
}
